import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Divider, IconButton, CircularProgress, Skeleton } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { getMenuItems } from '../../api/menus';
import { getFooterData } from '../../api/footer';
import { subscribeNewsletter } from '../../api/newsletter';
import { NAV_INDUSTRIES, NAV_PRODUCTS } from '../../data/navData';
import { CONTAINER_SX } from '../../style/containers';
import { withTrailingSlash } from '../../utils/url';

const PRIMARY = '#178ACB';
const FONT = 'Manrope, sans-serif';

const nameToSlug = (name) => (name || '').toLowerCase().replace(/\s+/g, '-');

const buildItemUrl = (item) => {
  if (item.url && item.url !== '#' && (item.type === 'url' || item.type === 'URL')) {
    return `/${item.url}`;
  }
  if (item.id) {
    const slugOrId = item.slug || item.category_slug || item.id;
    return `/categories/${slugOrId}`;
  }
  const name = item.name || item.label || '';
  return name ? `/${nameToSlug(name)}` : '#';
};

const ColHeading = ({ children }) => (
  <Typography sx={{
    color: PRIMARY,
    fontSize: { xs: 16, sm: 17, md: 'clamp(14px, 0.94vw, 18px)' },
    fontFamily: FONT,
    fontWeight: 600,
    lineHeight: 1.44,
  }}>
    {children}
  </Typography>
);

const ColLink = ({ to = '#', children }) => (
  <Typography
    component={RouterLink}
    to={to}
    sx={{
      color: 'black',
      fontSize: { xs: 13, sm: 13, md: 'clamp(11.5px, 0.73vw, 14px)' },
      fontFamily: FONT,
      fontWeight: 400,
      lineHeight: 1.43,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      '&:hover': { color: PRIMARY },
    }}
  >
    {children}
  </Typography>
);

const SOCIAL = [
  { iconSrc: `${process.env.PUBLIC_URL || ''}/assets/images/footer/facebook1.svg`, label: 'Facebook', href: 'https://www.facebook.com/dynalogofficial/' },
  { iconSrc: `${process.env.PUBLIC_URL || ''}/assets/images/footer/linkedin1.svg`, label: 'LinkedIn', href: 'https://in.linkedin.com/company/dynalog-india-ltd' },
  { iconSrc: `${process.env.PUBLIC_URL || ''}/assets/images/footer/insta1.svg`, label: 'Instagram', href: 'https://instagram.com/dynalogindia?igshid=YmMyMTA2M2Y=' },
  { iconSrc: `${process.env.PUBLIC_URL || ''}/assets/images/footer/twitter021.svg`, label: 'X / Twitter', href: 'https://twitter.com/dynalogindia' },
  { iconSrc: `${process.env.PUBLIC_URL || ''}/assets/images/footer/video1.svg`, label: 'YouTube', href: 'https://www.youtube.com/channel/UCVq-iErVLpzVHpUPHiDLsww/videos' },
];

const DEFAULT_COMPANY_LINKS = [
  { label: 'About Us', to: '/about-us' },
  { label: 'Partners', to: '/partners' },
  { label: 'Careers', to: '/careers' },
  { label: 'Events', to: '/events' },
  { label: 'Investors', to: '/investors' },
  { label: 'Contact Us', to: '/contact-us' },
];

// Row counts/widths below approximate today's real data (checked against the
// live API) so the loading state reserves close to the right amount of space
// per column — content will drift over time and won't line up pixel-perfect,
// but staying in the right ballpark is what avoids a large shift when the
// real content swaps in, unlike guessing one flat height for the whole footer.
const INDUSTRIES_SKELETON_WIDTHS = ['70%', '55%', '65%', '50%', '60%'];
const RESOURCES_SKELETON_WIDTHS = ['60%', '75%', '50%'];
// The address is real (long) text that wraps to ~2 lines on mobile — checked
// live against actual rendered height (37px ≈ 2 × 18.6px line-height) — while
// Call/WhatsApp/email are always single-line. A flat 1-line skeleton for the
// address under-reserved space, so the block grew when real data arrived and
// shifted the Social icons row directly below it (a residual CLS gap left by
// the original per-column skeleton fix — see docsguide/footer-cls-skeleton-loaders.md).
const GET_IN_TOUCH_SKELETON_ROWS = [
  { width: '90%', lines: 2 },
  { width: '65%' },
  { width: '70%' },
  { width: '60%' },
];

const ColLinkSkeleton = ({ width, lines = 1 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} variant="text" width={i === lines - 1 ? width : '95%'} height={18} />
    ))}
  </Box>
);

const BottomFooter = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState('idle'); // idle | loading | success | already_subscribed | error
  const [subError, setSubError] = useState('');
  const [subMessage, setSubMessage] = useState('');
  const [companyLinks, setCompanyLinks] = useState(DEFAULT_COMPANY_LINKS);
  const [industries, setIndustries] = useState([]);
  const [products, setProducts] = useState(NAV_PRODUCTS.map((c) => ({ label: c.label, url: c.url })));
  const [resources, setResources] = useState([]);
  const [getInTouch, setGetInTouch] = useState(null);

  useEffect(() => {
    getFooterData().then((data) => {
      if (data?.resources?.length) {
        setResources(data.resources.map((r) => ({ ...r, url: withTrailingSlash(r.url) })));
      }
      if (data?.getInTouch) setGetInTouch(data.getInTouch);
    });

    getMenuItems().then((items) => {
      // Company / URL-type links
      const urlLinks = items
        .filter((item) => item.is_active && (item.type === 'url' || item.type === 'URL'))
        .sort((a, b) => a.position - b.position)
        .map((item) => ({
          label: item.name,
          to: withTrailingSlash(item.url && item.url !== '#' ? `/${item.url}` : `/${nameToSlug(item.name)}`),
        }));
      if (urlLinks.length) {
        const hasCareer = urlLinks.some((l) => /career/i.test(l.label));
        if (!hasCareer) {
          const partnersIdx = urlLinks.findIndex((l) => /partners/i.test(l.label));
          const insertAt = partnersIdx >= 0 ? partnersIdx + 1 : urlLinks.length;
          urlLinks.splice(insertAt, 0, { label: 'Careers', to: '/careers/' });
        }
        setCompanyLinks(urlLinks);
      }

      // Industries — always set from API so deleted items don't linger from fallback
      const industriesMenu = items.find((m) => m.name === 'Industries');
      setIndustries(
        industriesMenu?.items?.length
          ? industriesMenu.items.map((item) => {
              const slug = item.slug || item.category_slug || nameToSlug(item.name || '');
              // A truthy item.url isn't necessarily a full path — for a
              // type:"url" menu item it's often just the bare slug an admin
              // typed in (e.g. "oil-and-gas"), which needs the /industries/
              // prefix like any other industry, not just a leading slash.
              const url = withTrailingSlash(
                item.url && item.url !== '#' && item.url.startsWith('/')
                  ? item.url
                  : `/industries/${(item.url && item.url !== '#') ? item.url : slug}`
              );
              return { label: item.name, url };
            })
          : []
      );

      // Products — top-level categories use /products/{slug}, matching the
      // header's own Products menu. The single-segment /products/:id vs flat
      // product-slug ambiguity this used to dodge (see CategoriesPage.js's
      // isBareIdRoute handling) is now resolved at the routing level, so this
      // no longer needs the /categories/{slug} workaround.
      const productsMenu = items.find((m) => m.name === 'Products');
      if (productsMenu?.items?.length) {
        setProducts(productsMenu.items.map((cat) => {
          const slug = cat.slug || cat.category_slug || nameToSlug(cat.name || '');
          return { label: cat.name, url: withTrailingSlash(`/products/${slug}`) };
        }));
      }
    });
  }, []);

  return (
    <Box component="footer" sx={{
      background: 'white',
      // Skip rendering until scrolled into view. The API call resolves (~1s) before
      // the user reaches the footer, so it paints once with final data — no CLS.
      // containIntrinsicSize reserves vertical space so content above doesn't shift.
      contentVisibility: 'auto',
      containIntrinsicSize: '0 800px',
    }}>
      {/* ── Nav + Contact grid ──────────────────────────────────────────────
          xs  (< 600px):  1 column — all sections stack vertically
          sm  (≥ 600px):  2 columns — side-by-side pairs
          md  (≥ 900px):  3 columns — nav + company + (contact+subscribe spans 2)
          lg  (≥ 1200px): 6 columns — full Figma layout
          
          Key fix: minmax(0, 1fr) forces each column to be at most its share
          of the container — text wraps instead of overflowing / cropping.
      ────────────────────────────────────────────────────────────────────── */}
      <Container maxWidth={false} sx={CONTAINER_SX}>
      <Box
        sx={{
          pt: { xs: 5, md: '80px', xl: '100px' },
          pb: { xs: 5, md: '48px', xl: '100px' },
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',           // 1 column: fully stacked
            sm: 'repeat(2, minmax(0,1fr))', // 2 columns
            md: 'repeat(3, minmax(0,1fr))', // 3 columns
            lg: 'minmax(0, 17fr) minmax(0, 21fr) minmax(0, 9fr) minmax(0, 9fr) minmax(0, 22fr) minmax(0, 22fr)',
          },
          gap: {
            xs: '32px 20px',
            sm: '40px 24px',
            md: '40px 32px',
            lg: '40px 40px',
            xl: `40px clamp(40px, 6.04vw, 116px)`, // 116px at 1920px; scales down at smaller xl viewports
          },
          // Ensure no child can bleed outside the container
          overflowX: 'hidden',
        }}
      >
        {/* Industries */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
          <ColHeading>Industries</ColHeading>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {industries.length > 0
              ? industries.map(({ label, url }) => (
                  <ColLink key={label} to={url}>{label}</ColLink>
                ))
              : INDUSTRIES_SKELETON_WIDTHS.map((width, i) => (
                  <ColLinkSkeleton key={i} width={width} />
                ))}
          </Box>
        </Box>

        {/* Products */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
          <ColHeading>Products</ColHeading>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {products.map(({ label, url }) => (
              <ColLink key={label} to={url}>{label}</ColLink>
            ))}
          </Box>
        </Box>

        {/* Company links */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
          {companyLinks.map(({ label, to }) => (
            <Typography
              key={label}
              component={RouterLink}
              to={to}
              sx={{
                color: PRIMARY,
                fontSize: { xs: 16, sm: 17, md: 'clamp(14px, 0.94vw, 18px)' },
                fontFamily: FONT,
                fontWeight: 600,
                lineHeight: 1.44,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { opacity: 0.8 },
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>

        {/* Resources */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
          <ColHeading>Resources</ColHeading>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {resources.length > 0
              ? resources.map(({ id, name, url }) => (
                  <ColLink key={id} to={url}>{name}</ColLink>
                ))
              : RESOURCES_SKELETON_WIDTHS.map((width, i) => (
                  <ColLinkSkeleton key={i} width={width} />
                ))}
          </Box>
        </Box>

        {/* Get in touch */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
          <ColHeading>Get in touch</ColHeading>
          {!getInTouch && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {GET_IN_TOUCH_SKELETON_ROWS.map((row, i) => (
                <ColLinkSkeleton key={i} width={row.width} lines={row.lines} />
              ))}
            </Box>
          )}
          {getInTouch && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Address — plain text, no link */}
              {getInTouch.address && (
                <Typography sx={{ color: 'black', fontSize: { xs: 13, sm: 13, md: 'clamp(11.5px, 0.73vw, 14px)' }, fontFamily: FONT, fontWeight: 400, lineHeight: 1.43, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  {getInTouch.address}
                </Typography>
              )}
              {/* Phone links — phone1 is a telephone number, phone2 is WhatsApp */}
              {[
                { label: 'Call', number: getInTouch.phone1, href: (n) => `tel:${n.replace(/\s/g, '')}` },
                { label: 'WhatsApp', number: getInTouch.phone2, href: (n) => `https://wa.me/${n.replace(/\D/g, '')}` },
              ].filter((row) => row.number).map(({ label, number, href }) => (
                <Box
                  key={label}
                  component="a"
                  href={href(number)}
                  target={label === 'WhatsApp' ? '_blank' : undefined}
                  rel={label === 'WhatsApp' ? 'noopener noreferrer' : undefined}
                  sx={{ display: 'flex', alignItems: 'baseline', gap: '5px', color: 'black', fontSize: { xs: 13, sm: 13, md: 'clamp(11.5px, 0.73vw, 14px)' }, fontFamily: FONT, fontWeight: 400, lineHeight: 1.43, textDecoration: 'none', '&:hover': { color: '#178ACB' } }}
                >
                  <Box component="span" sx={{ fontWeight: 600, flexShrink: 0 }}>{label}:</Box>
                  {number}
                </Box>
              ))}
              {/* Email link */}
              {getInTouch.email && (
                <Box
                  component="a"
                  href={`mailto:${getInTouch.email}`}
                  sx={{ color: 'black', fontSize: { xs: 13, sm: 13, md: 'clamp(11.5px, 0.73vw, 14px)' }, fontFamily: FONT, fontWeight: 400, lineHeight: 1.43, textDecoration: 'none', wordBreak: 'break-word', overflowWrap: 'anywhere', '&:hover': { color: '#178ACB' } }}
                >
                  {getInTouch.email}
                </Box>
              )}
            </Box>
          )}
          {/* Social icons */}
          <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '6px', opacity: 0.4 }}>
            {SOCIAL.map(({ iconSrc, label, href }) => (
              <Box
                key={label}
                component="a"
                href={href}
                aria-label={label}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, flexShrink: 0, '&:hover': { opacity: 0.7 } }}
              >
                <Box component="img" src={iconSrc} alt={label} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Stay Connected */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
          <ColHeading>Stay Connected</ColHeading>
          <Typography sx={{
            color: 'black',
            fontSize: { xs: 13, sm: 13, md: 'clamp(11.5px, 0.73vw, 14px)' },
            fontFamily: FONT,
            fontWeight: 400,
            lineHeight: 1.43,
            wordBreak: 'break-word',
          }}>
            Subscribe for the latest insights &amp; updates from Dynalog India. We maintain strict privacy standards and do not share your information.
          </Typography>
          {/* Email input */}
          <Box
            component="form"
            onSubmit={async (e) => {
              e.preventDefault();
              const trimmed = email.trim();
              if (!trimmed) return;
              setSubStatus('loading');
              setSubError('');
              try {
                const res = await subscribeNewsletter({ email: trimmed });
                if (res?.alreadySubscribed) {
                  setSubMessage(res.message || 'This email is already subscribed.');
                  setSubStatus('already_subscribed');
                } else {
                  setEmail('');
                  setSubStatus('idle');
                  navigate(withTrailingSlash('/subscription-confirmed'));
                }
              } catch (err) {
                const data = err?.response?.data;
                if (data?.alreadySubscribed) {
                  setSubMessage(data.message || 'This email is already subscribed.');
                  setSubStatus('already_subscribed');
                } else {
                  setSubError(data?.message || 'Something went wrong. Please try again.');
                  setSubStatus('error');
                }
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 64,
              pl: '12px',
              pr: '10px',
              background: 'white',
              borderRadius: '10px',
              border: subStatus === 'error' ? '1px solid #d32f2f' : subStatus === 'already_subscribed' ? '1px solid #ed6c02' : '1px solid rgba(0,0,0,0.10)',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            <Box
              component="input"
              type="email"
              placeholder="Enter Email Address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (subStatus !== 'idle') { setSubStatus('idle'); setSubError(''); setSubMessage(''); } }}
              disabled={subStatus === 'loading' || subStatus === 'success'}
              sx={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                fontSize: { xs: 13, sm: 13, md: 'clamp(11.5px, 0.73vw, 14px)' },
                fontFamily: FONT,
                color: 'black',
                background: 'transparent',
                '&::placeholder': { color: 'rgba(0,0,0,0.45)' },
                '&:disabled': { opacity: 0.6 },
              }}
            />
            <Box
              sx={{ width: '1px', height: 36, background: 'rgba(0,0,0,0.10)', mx: '8px', flexShrink: 0 }}
            />
            <IconButton
              size="small"
              type="submit"
              disabled={subStatus === 'loading' || subStatus === 'success' || subStatus === 'already_subscribed'}
              sx={{ p: '4px', flexShrink: 0 }}
              aria-label="Subscribe"
            >
              {subStatus === 'loading'
                ? <CircularProgress size={20} thickness={4} sx={{ color: PRIMARY }} />
                : <Box component="img" src={`${process.env.PUBLIC_URL || ''}/assets/images/footer/email-fly.svg`} alt="Subscribe" sx={{ width: 24, height: 24, display: 'block' }} />
              }
            </IconButton>
          </Box>
          {subStatus === 'success' && (
            <Typography sx={{ fontSize: 12, fontFamily: FONT, color: '#2e7d32' }}>
              Thank you for subscribing!
            </Typography>
          )}
          {subStatus === 'already_subscribed' && (
            <Typography sx={{ fontSize: 12, fontFamily: FONT, color: '#ed6c02' }}>
              {subMessage}
            </Typography>
          )}
          {subStatus === 'error' && (
            <Typography sx={{ fontSize: 12, fontFamily: FONT, color: '#d32f2f' }}>
              {subError}
            </Typography>
          )}
        </Box>
      </Box>
      </Container>

      {/* Copyright bar */}
      <Divider sx={{ borderColor: 'rgba(0,0,0,0.10)' }} />
      <Box sx={{ py: '25px', px: 2, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 14, fontFamily: FONT, fontWeight: 400, lineHeight: '20px', color: 'black' }}>
          © 2026&nbsp; All Rights Reserved, Dynalog India.&nbsp; |&nbsp;{' '}
          <Box component="span" sx={{ textDecoration: 'underline' }}>Web Design by</Box>
          {' '}Image Online Pvt. Ltd.&nbsp; |&nbsp;{' '}
          <Box component={RouterLink} to="/sitemap/" sx={{ color: 'inherit', textDecoration: 'underline' }}>Site Map</Box>
          &nbsp; |&nbsp;{' '}
          <Box component={RouterLink} to="/terms-and-conditions/" sx={{ color: 'inherit', textDecoration: 'underline' }}>Terms &amp; Conditions</Box>
          &nbsp; |&nbsp;{' '}
          <Box component={RouterLink} to="/privacy-policy/" sx={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default BottomFooter;
